import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { AlertCircle, Check, Database, GitBranch, Info, Loader2, Sparkles, X } from 'lucide-react';
import type {
  OntologyGenerationSettingsResponse,
  OntologyGenerationStreamState,
  OntologySuggestionResponse,
  SemanticContextOverviewResponse,
} from '../../hooks/useWorkspaceSemanticContext';
import { Alert, Badge, Button, Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle, Spinner } from '../ui';
import { OntologyBuildExperience } from './OntologyBuildExperience';
import { OntologyEditor } from './OntologyEditor';
import { suggestionArtifactRef } from './ontologyBuildProjection';
import type { StructuredAnalyticsDraft, WorkspaceOntology } from './structuredAnalyticsConfig';

export interface SemanticOntologyBuilderSurfaceProps {
  workspaceId?: number | string;
  draft: StructuredAnalyticsDraft;
  semanticContext?: SemanticContextOverviewResponse;
  generationSettings?: OntologyGenerationSettingsResponse;
  suggestions?: OntologySuggestionResponse[];
  generationStream?: OntologyGenerationStreamState;
  isSuggestionsLoading?: boolean;
  isDraftingSuggestions?: boolean;
  onSaveOntologyDraft: (ontology: WorkspaceOntology) => void | Promise<void>;
  onReviewDraft: () => void;
  hasReviewableRuntimeChanges?: boolean;
  onDraftWithAI?: () => void;
  onApplySuggestion?: (suggestionId: number) => void | Promise<unknown>;
  onApplySuggestionsBulk?: (suggestionIds: number[]) => void | Promise<unknown>;
  onRejectSuggestion?: (suggestionId: number) => void | Promise<unknown>;
  onOpenOverview: () => void;
}

export function SemanticOntologyBuilderSurface({
  workspaceId,
  draft,
  semanticContext,
  generationSettings,
  suggestions = [],
  generationStream,
  isSuggestionsLoading = false,
  isDraftingSuggestions = false,
  onSaveOntologyDraft,
  onReviewDraft,
  hasReviewableRuntimeChanges = false,
  onDraftWithAI,
  onApplySuggestion,
  onApplySuggestionsBulk,
  onRejectSuggestion,
  onOpenOverview,
}: SemanticOntologyBuilderSurfaceProps) {
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [resolvedProposalRefs, setResolvedProposalRefs] = useState<Set<string>>(
    () => new Set(),
  );
  const readiness = semanticContext?.readiness;
  const blockerCount =
    readiness?.blockers.filter((blocker) => blocker.severity === 'error').length ??
    draft.blockers.filter((blocker) => blocker.severity === 'error').length;
  const hasDraftContent =
    draft.ontology.entities.length > 0 ||
    draft.ontology.metrics.length > 0 ||
    draft.ontology.joins.length > 0;
  const reviewDisabled = !hasDraftContent;
  const hasInvalidSuggestionPayload = !Array.isArray(suggestions);
  const suggestionItems = Array.isArray(suggestions) ? suggestions : [];
  const pendingSuggestions = suggestionItems.filter((suggestion) => suggestion.status === 'pending');
  const latestPendingRunId = pendingSuggestions.reduce<number | null>(
    (latestRunId, suggestion) => (latestRunId == null || suggestion.run_id > latestRunId ? suggestion.run_id : latestRunId),
    null,
  );
  const visiblePendingSuggestions =
    latestPendingRunId == null
      ? pendingSuggestions
      : pendingSuggestions.filter((suggestion) => suggestion.run_id === latestPendingRunId);
  const hiddenStaleSuggestionCount = pendingSuggestions.length - visiblePendingSuggestions.length;
  const streamRunId = generationStream?.runId;
  useEffect(() => {
    setResolvedProposalRefs(new Set());
  }, [streamRunId]);
  const streamSuggestions =
    streamRunId == null ? [] : suggestionItems.filter((suggestion) => suggestion.run_id === streamRunId);
  const hiddenProposalRefs = new Set(
    [
      ...Array.from(resolvedProposalRefs),
      ...streamSuggestions
        .filter((suggestion) => suggestion.status !== 'pending')
        .map(suggestionArtifactRef),
    ],
  );
  const showGenerationExperience = Boolean(
    generationStream &&
      (
        generationStream.isStreaming ||
        generationStream.events.length > 0 ||
        generationStream.isPersistedReplay ||
        generationStream.connectionState === 'permission_denied'
      ) &&
      (
        !generationStream.completion ||
        generationStream.isPersistedReplay ||
        streamSuggestions.some((suggestion) => suggestion.status === 'pending')
      ),
  );
  const recordResolvedSuggestions = (suggestionIds: number[]) => {
    const resolvedRefs = suggestionItems
      .filter((suggestion) => suggestionIds.includes(suggestion.id))
      .map(suggestionArtifactRef);
    if (resolvedRefs.length === 0) return;
    setResolvedProposalRefs((current) => new Set([...Array.from(current), ...resolvedRefs]));
  };
  const handleApplySuggestion = onApplySuggestion
    ? async (suggestionId: number) => {
        await onApplySuggestion(suggestionId);
        recordResolvedSuggestions([suggestionId]);
      }
    : undefined;
  const handleApplySuggestionsBulk = onApplySuggestionsBulk
    ? async (suggestionIds: number[]) => {
        await onApplySuggestionsBulk(suggestionIds);
        recordResolvedSuggestions(suggestionIds);
      }
    : undefined;
  const handleRejectSuggestion = onRejectSuggestion
    ? async (suggestionId: number) => {
        await onRejectSuggestion(suggestionId);
        recordResolvedSuggestions([suggestionId]);
      }
    : undefined;

  return (
    <div className="flex h-full flex-col gap-3" data-testid="semantic-ontology-builder-surface">
      {hasInvalidSuggestionPayload ? (
        <Alert variant="error">
          Ontology suggestions could not be displayed because the server response was invalid. Refresh the page to retry.
        </Alert>
      ) : null}
      <OntologyEditor
        ontology={draft.ontology}
        allowlist={draft.allowlist}
        onClose={onOpenOverview}
        onSave={onSaveOntologyDraft}
        onReviewDraft={onReviewDraft}
        hasReviewableRuntimeChanges={hasReviewableRuntimeChanges}
        reviewDisabled={reviewDisabled}
        reviewDisabledReason="Add at least one ontology item before reviewing a Runtime Context Pack."
        readinessLabel={readiness?.status.replace('_', ' ') ?? draft.readiness.replace('_', ' ')}
        runtimePackLabel={draft.activeRuntimePackVersion ?? 'No active pack'}
        selectedAssetCount={readiness?.selected_table_count ?? draft.selectedAssetCount}
        backendBlockerCount={blockerCount}
        aiDraftDisabled={!generationSettings?.policy.enabled || Boolean(generationSettings?.disabled_reason) || !onDraftWithAI}
        aiDraftDisabledReason={generationSettings?.disabled_reason ?? 'Ontology auto-build is not connected on this surface.'}
        isAiDrafting={isDraftingSuggestions}
        isAiDraftFailed={Boolean(generationStream?.failure)}
        aiDraftProgressMessage={generationStream?.failure?.message ?? generationStream?.progressMessage}
        aiDraftProgressStage={generationStream?.failure?.stage_name ?? generationStream?.latestEvent?.stage_name ?? undefined}
        aiDraftProgressPercent={generationStream?.failure ? 100 : generationStream?.progressPercentage}
        isAiDraftStreamConnected={generationStream?.isConnected}
        onDraftWithAI={onDraftWithAI}
        layoutStorageKey={buildOntologyLayoutStorageKey(workspaceId, draft.ontologyVersionLabel)}
        generationExperience={
          showGenerationExperience && generationStream ? (
            <OntologyBuildExperience
              stream={generationStream}
              hiddenArtifactRefs={hiddenProposalRefs}
              onReviewChanges={() => setIsReviewOpen(true)}
            />
          ) : undefined
        }
        suggestionReviewAction={
          <OntologySuggestionReviewAction
            isOpen={isReviewOpen}
            onOpenChange={setIsReviewOpen}
            pendingSuggestions={visiblePendingSuggestions}
            hiddenStaleSuggestionCount={hiddenStaleSuggestionCount}
            generationStream={generationStream}
            isDraftingSuggestions={isDraftingSuggestions}
            isLoading={isSuggestionsLoading}
            onApplySuggestion={handleApplySuggestion}
            onApplySuggestionsBulk={handleApplySuggestionsBulk}
            onRejectSuggestion={handleRejectSuggestion}
          />
        }
      />
    </div>
  );
}

function buildOntologyLayoutStorageKey(
  workspaceId: number | string | undefined,
  ontologyVersionLabel: string | null,
): string | undefined {
  if (workspaceId == null || workspaceId === '') return undefined;
  const version = ontologyVersionLabel?.trim() || 'draft';
  return `workspace-ontology-layout:${workspaceId}:${version}`;
}

function OntologySuggestionReviewAction({
  isOpen,
  onOpenChange,
  pendingSuggestions,
  hiddenStaleSuggestionCount,
  generationStream,
  isDraftingSuggestions,
  isLoading,
  onApplySuggestion,
  onApplySuggestionsBulk,
  onRejectSuggestion,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  pendingSuggestions: OntologySuggestionResponse[];
  hiddenStaleSuggestionCount: number;
  generationStream?: OntologyGenerationStreamState;
  isDraftingSuggestions: boolean;
  isLoading: boolean;
  onApplySuggestion?: (suggestionId: number) => void | Promise<unknown>;
  onApplySuggestionsBulk?: (suggestionIds: number[]) => void | Promise<unknown>;
  onRejectSuggestion?: (suggestionId: number) => void | Promise<unknown>;
}) {
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  const pendingCount = pendingSuggestions.length;
  const groupedSuggestions = useMemo(() => groupSuggestionsForReview(pendingSuggestions), [pendingSuggestions]);
  const orderedSuggestionIds = useMemo(
    () => groupedSuggestions.flatMap((group) => group.suggestions.map((suggestion) => suggestion.id)),
    [groupedSuggestions],
  );
  const handleAcceptAll = async () => {
    if ((!onApplySuggestionsBulk && !onApplySuggestion) || orderedSuggestionIds.length === 0 || isApplyingAll) return;
    setIsApplyingAll(true);
    try {
      if (onApplySuggestionsBulk) {
        await onApplySuggestionsBulk(orderedSuggestionIds);
      } else if (onApplySuggestion) {
        for (const suggestionId of orderedSuggestionIds) {
          await onApplySuggestion(suggestionId);
        }
      }
    } finally {
      setIsApplyingAll(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onOpenChange(true)}
        disabled={isLoading || pendingCount === 0}
        className="relative"
      >
        {isLoading ? <Spinner size="sm" /> : <Sparkles className="h-4 w-4" />}
        Review suggestions
        {pendingCount > 0 ? (
          <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-[11px] font-semibold leading-none text-white">
            {pendingCount}
          </span>
        ) : null}
      </Button>
      <Sheet open={isOpen} onClose={() => onOpenChange(false)}>
        <SheetContent side="right" size="2xl">
          <SheetHeader>
            <SheetTitle>Suggestion queue</SheetTitle>
            <SheetDescription>
              AI suggestions are isolated from the ontology draft until a steward applies them.
            </SheetDescription>
          </SheetHeader>
          <SheetBody className="space-y-3">
            {pendingSuggestions.length > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-dark-border dark:bg-dark-surface">
                <div>
                  <p className="text-sm font-semibold text-charcoal dark:text-gray-100">
                    {pendingCount} pending suggestion{pendingCount === 1 ? '' : 's'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Apply every visible suggestion from the current AI run.
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAcceptAll}
                  disabled={(!onApplySuggestionsBulk && !onApplySuggestion) || isApplyingAll}
                >
                  {isApplyingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  Accept all
                </Button>
              </div>
            ) : null}
            <GenerationReviewStatus
              generationStream={generationStream}
              isDraftingSuggestions={isDraftingSuggestions}
              hiddenStaleSuggestionCount={hiddenStaleSuggestionCount}
            />
            {pendingSuggestions.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 p-5 text-sm text-gray-500 dark:border-dark-border dark:text-gray-400">
                No pending suggestions.
              </div>
            ) : (
              groupedSuggestions.map((group) => (
                <section key={group.label} className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                      {group.label}
                    </h3>
                    <Badge variant="secondary">{group.suggestions.length}</Badge>
                  </div>
                  {group.suggestions.map((suggestion) => (
                    <SuggestionReviewCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      onApplySuggestion={onApplySuggestion}
                      onRejectSuggestion={onRejectSuggestion}
                    />
                  ))}
                </section>
              ))
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
}

function GenerationReviewStatus({
  generationStream,
  isDraftingSuggestions,
  hiddenStaleSuggestionCount,
}: {
  generationStream?: OntologyGenerationStreamState;
  isDraftingSuggestions: boolean;
  hiddenStaleSuggestionCount: number;
}) {
  if (generationStream?.failure) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs leading-5 text-rose-700 dark:border-rose-800 dark:bg-rose-900/30 dark:text-rose-300">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">Generation failed</p>
            <p>{generationStream.failure.message || 'Review the latest generation event and try again.'}</p>
          </div>
        </div>
      </div>
    );
  }
  if (isDraftingSuggestions) {
    return (
      <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs leading-5 text-sky-700 dark:border-sky-800 dark:bg-sky-900/30 dark:text-sky-300">
        <div className="flex items-start gap-2">
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
          <div>
            <p className="font-semibold">Generating suggestions</p>
            <p>{generationStream?.progressMessage || 'Waiting for the next ontology generation event.'}</p>
          </div>
        </div>
      </div>
    );
  }
  if (hiddenStaleSuggestionCount > 0) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{hiddenStaleSuggestionCount} older pending suggestion{hiddenStaleSuggestionCount === 1 ? ' is' : 's are'} hidden while reviewing the newest AI run.</p>
        </div>
      </div>
    );
  }
  return null;
}

function SuggestionReviewCard({
  suggestion,
  onApplySuggestion,
  onRejectSuggestion,
}: {
  suggestion: OntologySuggestionResponse;
  onApplySuggestion?: (suggestionId: number) => void | Promise<unknown>;
  onRejectSuggestion?: (suggestionId: number) => void | Promise<unknown>;
}) {
  const payload = suggestion.proposed_payload;
  const isModelBacked = suggestion.model_metadata?.generation_source === 'model_stage';
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-charcoal dark:text-gray-100">
              {suggestionLabel(suggestion)}
            </p>
            {isModelBacked ? <Badge variant="info">Model-backed</Badge> : null}
          </div>
          <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
            {suggestionReviewDescription(suggestion)}
          </p>
        </div>
        <Badge variant="secondary">{Math.round(suggestion.confidence_score * 100)}%</Badge>
      </div>

      <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
        <ReviewFact label="Target" value={suggestionTargetDetail(suggestion) || 'No target reference provided'} mono />
        <ReviewFact label="Effect" value={suggestionEffectDescription(suggestion)} />
      </div>

      <SuggestionTypeDetails suggestion={suggestion} />

      <div className="mt-3 rounded-md bg-gray-50 p-3 text-xs leading-5 text-gray-600 dark:bg-dark-surface-2 dark:text-gray-300">
        {suggestionKindDescription(suggestion)}
      </div>

      {dependencyHint(payload) ? (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
          {dependencyHint(payload)}
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onRejectSuggestion?.(suggestion.id)}
          disabled={!onRejectSuggestion}
        >
          <X className="h-3.5 w-3.5" />
          Reject
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => onApplySuggestion?.(suggestion.id)}
          disabled={!onApplySuggestion}
        >
          <Check className="h-3.5 w-3.5" />
          Apply to draft
        </Button>
      </div>
    </div>
  );
}

function SuggestionTypeDetails({ suggestion }: { suggestion: OntologySuggestionResponse }) {
  const payload = suggestion.proposed_payload;
  const evidence = suggestion.evidence_refs ?? {};

  if (suggestion.target_type === 'entity') {
    const sourceBindings = arrayRecords(payload.source_bindings);
    return (
      <ReviewSection icon={<Database className="h-3.5 w-3.5" />} title="Entity grain and backing assets">
        <div className="grid gap-2 sm:grid-cols-2">
          <ReviewFact label="Logical key" value={stringValue(payload.name) || stringValue(payload.model_entity_key)} mono />
          <ReviewFact label="Primary table" value={idLabel('table', payload.table_id ?? evidence.primary_warehouse_table_id)} mono />
        </div>
        <ChipList
          label="Backing table ids"
          values={arrayValues(payload.source_table_ids ?? evidence.warehouse_table_ids).map((value) => idLabel('table', value))}
        />
        <ChipList label="Aliases" values={arrayValues(payload.aliases)} />
        {sourceBindings.length > 0 ? (
          <div className="space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
              Binding evidence
            </p>
            {sourceBindings.map((binding, index) => (
              <div
                key={`${String(binding.table_id)}-${index}`}
                className="rounded-md border border-gray-200 bg-white p-2.5 dark:border-dark-border dark:bg-dark-surface"
              >
                <div className="grid gap-2 sm:grid-cols-2">
                  <ReviewFact label="Table" value={idLabel('table', binding.table_id)} mono />
                  <ReviewFact label="Role" value={stringValue(binding.binding_role ?? binding.role)} />
                  <ReviewFact label="Match" value={stringValue(binding.match_method)} />
                  <ReviewFact label="Review state" value={stringValue(binding.review_state)} />
                  <ReviewFact label="Authority" value={stringValue(binding.source_authority)} />
                  <ReviewFact label="Execution" value={stringValue(binding.execution_mode)} />
                  <ReviewFact label="Confidence" value={
                    typeof binding.confidence === 'number'
                      ? `${Math.round(binding.confidence * 100)}%`
                      : ''
                  } />
                  <ReviewFact label="Conflict" value={stringValue(binding.conflict_state) || 'none'} />
                </div>
                <ChipList
                  label="Identity evidence"
                  values={arrayRecords(binding.identity_columns ?? binding.keys).map((item) =>
                    arrayValues(item.column_names).join(' + ') || idLabel('column', arrayValues(item.column_ids)[0]),
                  )}
                />
                <ChipList
                  label="Typed identity mappings"
                  values={arrayRecords(binding.identity_mappings)
                    .map((item) => stringValue(item.mapping_type))
                    .filter((value): value is string => value != null)}
                />
                <ChipList
                  label="Evidence"
                  values={arrayRecords(binding.match_evidence)
                    .map((item) => stringValue(item.type))
                    .filter((value): value is string => value != null)}
                />
              </div>
            ))}
          </div>
        ) : null}
      </ReviewSection>
    );
  }

  if (suggestion.target_type === 'semantic_mapping') {
    return (
      <ReviewSection icon={<Info className="h-3.5 w-3.5" />} title="Business meaning evidence">
        <div className="grid gap-2 sm:grid-cols-2">
          <ReviewFact label="Meaning key" value={stringValue(payload.concept_key ?? suggestion.target_ref?.meaning_key)} mono />
          <ReviewFact label="Entity key" value={stringValue(payload.entity_key ?? evidence.entity_key)} mono />
          <ReviewFact label="Catalog column" value={catalogColumnRef(payload, evidence)} mono />
          <ReviewFact label="Role" value={stringValue(payload.value_kind)} />
          <ReviewFact label="Default aggregation" value={stringValue(payload.default_aggregation)} />
        </div>
        <ChipList label="Aliases" values={arrayValues(payload.aliases)} />
      </ReviewSection>
    );
  }

  if (suggestion.target_type === 'metric') {
    return (
      <ReviewSection icon={<Sparkles className="h-3.5 w-3.5" />} title="Metric semantic inputs">
        <div className="grid gap-2 sm:grid-cols-2">
          <ReviewFact label="Entity key" value={stringValue(payload.entity_key ?? evidence.entity_key)} mono />
          <ReviewFact label="Primary column" value={idLabel('column', payload.primary_column_id ?? evidence.primary_warehouse_column_id)} mono />
          <ReviewFact label="Expression" value={stringValue(payload.sql_formula)} mono />
          <ReviewFact label="Format" value={stringValue(payload.format_type)} />
        </div>
        <ChipList
          label="Business meanings"
          values={[
            ...arrayValues(evidence.meaning_keys),
            ...arrayValues(nestedValue(payload.expression_metadata, ['semantic_mapping_concept_keys'])),
          ]}
        />
      </ReviewSection>
    );
  }

  if (suggestion.target_type === 'relationship' || suggestion.target_type === 'join') {
    const joinConditions = arrayRecords(payload.join_conditions ?? evidence.join_conditions);
    return (
      <ReviewSection icon={<GitBranch className="h-3.5 w-3.5" />} title="Relationship contract and join evidence">
        <div className="grid gap-2 sm:grid-cols-2">
          <ReviewFact label="Predicate" value={stringValue(payload.predicate ?? payload.display_name)} />
          <ReviewFact label="Kind" value={stringValue(payload.relationship_kind)} />
          <ReviewFact label="Source entity" value={stringValue(payload.source_entity_key ?? evidence.source_entity_key)} mono />
          <ReviewFact label="Target entity" value={stringValue(payload.target_entity_key ?? evidence.target_entity_key)} mono />
          <ReviewFact label="Cardinality" value={stringValue(payload.cardinality ?? payload.relationship_type)} />
          <ReviewFact label="Join evidence" value={joinEvidenceSummary(payload, joinConditions)} mono />
        </div>
        {joinConditions.length > 0 ? (
          <div className="mt-2 space-y-1">
            {joinConditions.slice(0, 3).map((condition, index) => (
              <div
                key={`${condition.source_column_id ?? index}-${condition.target_column_id ?? index}`}
                className="rounded-md border border-gray-200 bg-white px-2.5 py-2 font-mono text-[11px] text-gray-600 dark:border-dark-border dark:bg-dark-surface dark:text-gray-300"
              >
                {idLabel('table', condition.source_table_id)}.{idLabel('column', condition.source_column_id)}
                {' = '}
                {idLabel('table', condition.target_table_id)}.{idLabel('column', condition.target_column_id)}
              </div>
            ))}
          </div>
        ) : null}
      </ReviewSection>
    );
  }

  return null;
}

function ReviewSection({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function ReviewFact({
  label,
  value,
  mono = false,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`mt-1 break-words text-xs text-gray-700 dark:text-gray-300 ${mono ? 'font-mono text-[11px]' : ''}`}>
        {value || 'Not specified'}
      </p>
    </div>
  );
}

function ChipList({ label, values }: { label: string; values: string[] }) {
  const uniqueValues = Array.from(new Set(values.filter(Boolean))).slice(0, 8);
  if (uniqueValues.length === 0) return null;
  return (
    <div className="mt-2">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">{label}</p>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {uniqueValues.map((value) => (
          <Badge key={value} variant="outline" className="max-w-full truncate">
            {value}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function groupSuggestionsForReview(suggestions: OntologySuggestionResponse[]): Array<{
  label: string;
  suggestions: OntologySuggestionResponse[];
}> {
  const order = ['entity', 'semantic_mapping', 'metric', 'relationship', 'join'];
  return order
    .map((targetType) => ({
      label: suggestionGroupLabel(targetType),
      suggestions: suggestions.filter((suggestion) => suggestion.target_type === targetType),
    }))
    .filter((group) => group.suggestions.length > 0);
}

function suggestionLabel(suggestion: OntologySuggestionResponse): string {
  const payload = suggestion.proposed_payload;
  const displayName =
    typeof payload.display_name === 'string'
      ? payload.display_name
      : typeof payload.label === 'string'
        ? payload.label
        : typeof payload.name === 'string'
          ? humanizeIdentifier(payload.name)
          : humanizeIdentifier(suggestion.target_type);
  return `${suggestionArtifactLabel(suggestion.target_type)}: ${displayName}`;
}

function suggestionTargetDetail(suggestion: OntologySuggestionResponse): string {
  const payload = suggestion.proposed_payload;
  if (typeof payload.entity_key === 'string') {
    return payload.entity_key;
  }
  if (typeof payload.concept_key === 'string') {
    return payload.concept_key;
  }
  if (typeof payload.sql_formula === 'string') {
    return payload.sql_formula;
  }
  if (typeof payload.join_on === 'string') {
    return payload.join_on;
  }
  const tableId = suggestion.target_ref?.table_id;
  const columnId = suggestion.target_ref?.column_id;
  return [tableId ? `table ${tableId}` : null, columnId ? `column ${columnId}` : null]
    .filter(Boolean)
    .join(' · ');
}

function suggestionKindDescription(suggestion: OntologySuggestionResponse): string {
  const type = suggestion.target_type.replace('_', ' ');
  if (suggestion.target_type === 'semantic_mapping') {
    return 'Adds a reusable business meaning that links business language to a specific catalog column.';
  }
  if (suggestion.target_type === 'metric') {
    return 'Adds a governed metric expression that runtime query planning can use after pack review.';
  }
  if (suggestion.target_type === 'entity') {
    return 'Adds or updates a logical business entity and its catalog table mapping.';
  }
  if (suggestion.target_type === 'join' || suggestion.target_type === 'relationship') {
    return 'Adds a physical join path as evidence for a safe relationship between ontology entities.';
  }
  return `${suggestion.operation} suggestion for a ${type} artifact.`;
}

function suggestionReviewDescription(suggestion: OntologySuggestionResponse): string {
  if (suggestion.rationale) {
    return suggestion.rationale;
  }
  return 'I suggested this artifact from the selected warehouse scope and catalog metadata. Review it before applying it to the ontology draft.';
}

function suggestionArtifactLabel(targetType: string): string {
  switch (targetType) {
    case 'entity':
      return 'Entity';
    case 'semantic_mapping':
      return 'Business meaning';
    case 'metric':
      return 'Metric';
    case 'relationship':
    case 'join':
      return 'Relationship';
    default:
      return humanizeIdentifier(targetType);
  }
}

function suggestionGroupLabel(targetType: string): string {
  switch (targetType) {
    case 'entity':
      return 'Entities';
    case 'semantic_mapping':
      return 'Business meanings';
    case 'metric':
      return 'Metrics';
    case 'relationship':
    case 'join':
      return 'Relationships';
    default:
      return humanizeIdentifier(targetType);
  }
}

function humanizeIdentifier(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function suggestionEffectDescription(suggestion: OntologySuggestionResponse): string {
  const operation = suggestion.operation.toLowerCase();
  if (operation === 'create') {
    return 'Creates a new draft item only after approval.';
  }
  if (operation === 'update') {
    return 'Updates the matching draft item only after approval.';
  }
  if (operation === 'delete') {
    return 'Removes the matching draft item only after approval.';
  }
  return 'Applies a governed draft change only after approval.';
}

function dependencyHint(payload: Record<string, unknown>): string | null {
  if (typeof payload.source_entity_key === 'string' || typeof payload.target_entity_key === 'string') {
    return 'Apply the referenced logical entity suggestions before applying this relationship.';
  }
  if (typeof payload.entity_key === 'string') {
    return 'Apply the referenced logical entity suggestion before applying this dependent object.';
  }
  return null;
}

function stringValue(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

function arrayValues(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => stringValue(item))
    .filter((item): item is string => Boolean(item));
}

function arrayRecords(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object' && !Array.isArray(item));
}

function nestedValue(value: unknown, path: string[]): unknown {
  let current = value;
  for (const segment of path) {
    if (!current || typeof current !== 'object' || Array.isArray(current)) return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function idLabel(kind: string, value: unknown): string {
  const text = stringValue(value);
  return text ? `${kind} ${text}` : 'Not specified';
}

function catalogColumnRef(payload: Record<string, unknown>, evidence: Record<string, unknown>): string {
  const tableId = payload.table_id ?? evidence.warehouse_table_id;
  const columnId = payload.column_id ?? evidence.warehouse_column_id;
  return [idLabel('table', tableId), idLabel('column', columnId)]
    .filter((value) => value !== 'Not specified')
    .join(' · ') || 'Not specified';
}

function joinEvidenceSummary(payload: Record<string, unknown>, joinConditions: Array<Record<string, unknown>>): string {
  const joinOn = stringValue(payload.join_on);
  if (joinOn) return joinOn;
  if (joinConditions.length > 0) {
    return `${joinConditions.length} structured join condition${joinConditions.length === 1 ? '' : 's'}`;
  }
  return 'Semantic-only relationship; SQL traversal requires validated join evidence.';
}
